import yaml from "js-yaml"

/**
 * Converts YAML string to Properties format
 */
export function yamlToProperties(yamlString) {
  try {
    // Parse YAML
    const parsedYaml = yaml.load(yamlString)

    if (!parsedYaml || typeof parsedYaml !== "object") {
      throw new Error("Invalid YAML input")
    }

    // Convert to properties format
    const properties = []
    flattenObject(parsedYaml, "", properties)

    return properties.join("\n")
  } catch (error) {
    throw new Error(`Failed to parse YAML: ${error.message}`)
  }
}

/**
 * Converts Properties string to YAML format
 */
export function propertiesToYaml(propertiesString) {
  try {
    // Parse properties
    const lines = propertiesString.split("\n")
    const obj = {}

    for (const line of lines) {
      const trimmedLine = line.trim()

      // Skip empty lines and comments
      if (!trimmedLine || trimmedLine.startsWith("#")) {
        continue
      }

      // Find the first equals sign that's not escaped
      const equalsIndex = findUnescapedChar(trimmedLine, "=")

      if (equalsIndex === -1) {
        continue // Skip lines without equals sign
      }

      const key = trimmedLine.substring(0, equalsIndex).trim()
      let value = trimmedLine.substring(equalsIndex + 1).trim()

      // Handle value types (convert strings to appropriate types)
      if (value === "true" || value === "false") {
        value = value === "true"
      } else if (!isNaN(Number(value)) && value !== "") {
        value = Number(value)
      }

      // Build nested object structure
      setNestedProperty(obj, key, value)
    }

    // Convert to YAML
    return yaml.dump(obj, {
      lineWidth: -1, // Don't wrap lines
      noRefs: true, // Don't use references
      quotingType: '"', // Use double quotes
    })
  } catch (error) {
    throw new Error(`Failed to parse Properties: ${error.message}`)
  }
}

/**
 * Recursively flattens an object into properties format
 */
function flattenObject(obj, prefix, result) {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newPrefix = prefix ? `${prefix}.${key}` : key

      if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
        flattenObject(obj[key], newPrefix, result)
      } else {
        // Handle arrays by converting them to comma-separated strings
        let value = obj[key]
        if (Array.isArray(value)) {
          value = value.join(",")
        }
        result.push(`${newPrefix}=${value}`)
      }
    }
  }
}

/**
 * Sets a nested property in an object using dot notation
 */
function setNestedProperty(obj, path, value) {
  const parts = path.split(".")
  let current = obj

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    if (!current[part]) {
      current[part] = {}
    }
    current = current[part]
  }

  current[parts[parts.length - 1]] = value
}

/**
 * Finds the first unescaped occurrence of a character in a string
 */
function findUnescapedChar(str, char) {
  let i = 0
  while (i < str.length) {
    if (str[i] === "\\") {
      i += 2 // Skip the escaped character
      continue
    }
    if (str[i] === char) {
      return i
    }
    i++
  }
  return -1
}
